import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { pageViewEvent, pageViewEventPixelSDK } from './user-pixel';
import { useRouter } from 'next/router';
import { environment } from '../../environment/environment';

const isBrowser = typeof window !== 'undefined';

const PIXEL_SCRIPT_ID = 'tiktok-pixel-script';
const ANALYTICS_URL = 'https://analytics.tiktok.com/i18n/pixel/events.js';

/**
 * Higher Order Component for TikTok Pixel tracking
 * @param pixelId - The TikTok pixel ID
 * @param pixelName - Name for tracking purposes
 */
export const withPixelTracking =
  ({ pixelId, pixelName }) =>
  (WrappedComponent: React.ComponentType<any>) =>
  (props: any) => {
    const router = useRouter();
    const pixelLoadedRef = React.useRef(false);
    const eventSentRef = React.useRef(false);
    const validToInjectScript = environment.REACT_APP_ENV === 'PROD';

    React.useEffect(() => {
      if (!router.isReady || !validToInjectScript) return;

      // Function to load the TikTok pixel script
      const loadPixelScript = () => {
        if (pixelLoadedRef.current || !isBrowser) return;

        // Check if script already exists
        if (document.getElementById(PIXEL_SCRIPT_ID)) {
          pixelLoadedRef.current = true;
          return;
        }

        // Create the TikTok pixel script
        const pixelCode = `
!function (w, d, t) {
  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="${ANALYTICS_URL}",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};

  ttq.load('${pixelId}');
}(window, document, 'ttq');`;

        const script = document.createElement('script');
        script.id = PIXEL_SCRIPT_ID;
        script.type = 'text/javascript';
        script.text = pixelCode;
        script.async = true;
        document.head.appendChild(script);

        pixelLoadedRef.current = true;
      };

      // Function to handle page view tracking
      const trackPageView = () => {
        if (eventSentRef.current || !isBrowser) return;

        try {
          // Get user information from localStorage
          const currentUserData = localStorage.getItem('auth.currentuser.custom_claim');
          const userMetadata = currentUserData ? JSON.parse(currentUserData)?.userMetadata : null;

          // Generate unique event ID
          const eventId = uuidv4();

          // Send tracking events
          pageViewEventPixelSDK(eventId);
          pageViewEvent(eventId, pixelName, userMetadata?.email, userMetadata?.phone);

          eventSentRef.current = true;
        } catch (error) {
          console.error('Error tracking page view:', error);
        }
      };

      // Main handler for route changes
      const handleRouteChange = () => {
        loadPixelScript();
        trackPageView();
      };

      // Initial tracking on component mount
      handleRouteChange();

      // Setup listener for route changes
      router.events.on('routeChangeComplete', handleRouteChange);

      // Cleanup listener on unmount
      return () => {
        router.events.off('routeChangeComplete', handleRouteChange);
      };
    }, [router.isReady, router.events, validToInjectScript]);

    return <WrappedComponent {...props} />;
  };
